import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { ComponentProps, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { Dots } from '@sorare/core/src/atoms/navigation/Dots';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { LabelM, LabelXS } from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Ordinal } from '@sorare/core/src/atoms/typography/Ordinal';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { TrophyIcon } from '@sorare/core/src/components/club/Trophies';
import { PlaceHolderAvatar } from '@sorare/core/src/components/user/Avatar';
import { parseRankingRewards } from '@sorare/core/src/lib/rewards';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { ColoredRanking } from 'components/leaderboard/CumulativeRankRewards/RankRewardTitle/ColoredRanking';
import { LeagueSquareLink } from 'components/league/LeagueSquareLink';
import {
  NewAnyRewardConfig,
  Variant,
} from 'components/rewards/NewAnyRewardConfig';

import type { LivePastLeaderboard_so5LeaderboardGroup } from './__generated__/index.graphql';

const RightArrow = styled(IconButton)`
  position: absolute;
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
`;
const LeftArrow = styled(IconButton)`
  position: absolute;
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
`;
const BrandSup = styled(LabelXS).attrs({ brand25: true, as: 'sup' })``;
const TopLeaderboard = styled(Vertical)`
  width: 100%;
  min-width: 100%;
  position: relative;
  padding: 0 var(--double-unit);
  cursor: pointer;

  &:hover {
    ${LeftArrow}, ${RightArrow} {
      opacity: 1;
    }
  }
`;
const Content = styled(Vertical)`
  width: 100%;
  max-width: 100%;
  padding: var(--double-unit) var(--unit) 0 var(--unit);
  z-index: 1;

  & > *:first-child {
    max-width: 100%;
    width: 100%;
  }
`;
const Rewards = styled(Horizontal)`
  flex-shrink: 0;
`;

type Props = {
  so5LeaderboardGroup: LivePastLeaderboard_so5LeaderboardGroup;
} & Pick<ComponentProps<typeof LeagueSquareLink>, 'onClick' | 'to'>;
export const LivePastLeaderboard = ({
  to,
  so5LeaderboardGroup,
  onClick,
}: Props) => {
  const [selectedSo5LeaderboardIndex, setSelectedSo5LeaderboardIndex] =
    useState<number>(0);

  const selectedSo5Leaderboard =
    so5LeaderboardGroup.so5Leaderboards[selectedSo5LeaderboardIndex];

  const { mainRarityType, division, divisionChangeConfiguration } =
    selectedSo5Leaderboard || {};

  const hasDivisionConfiguration = !!divisionChangeConfiguration;

  const leadearboardsWithRanking = so5LeaderboardGroup.so5Leaderboards.filter(
    leaderboard => (leaderboard.rewardsConfig.ranking || []).length > 0
  );

  return (
    <LeagueSquareLink
      so5LeaderboardGroupInterface={so5LeaderboardGroup}
      forceShowBackground
      logoProps={{
        width: 200,
        height: 89,
      }}
      topLeft={
        <Chip>
          <ScarcityIcon rarity={mainRarityType || 'mix'} />
          {!!hasDivisionConfiguration && (
            <FormattedMessage
              id="so5.leaderboard.division"
              defaultMessage="Div {division}"
              values={{ division }}
            />
          )}{' '}
          <FormattedMessage {...scarcityMessages[mainRarityType || 'mix']} />
        </Chip>
      }
      to={to}
      onClick={onClick}
    >
      <Content center gap={3}>
        <Scrollable
          itemToDisplay={1}
          itemGap={4}
          withScrollButtons
          onVisibleItemsChanged={items => {
            setSelectedSo5LeaderboardIndex(items[0]);
          }}
          indexToScroll={selectedSo5LeaderboardIndex}
        >
          {leadearboardsWithRanking.map(so5Leaderboard => {
            if (!so5Leaderboard?.rewardsConfig.ranking) return null;
            return (
              <TopLeaderboard
                key={so5Leaderboard.slug}
                as="button"
                onClick={onClick}
              >
                {(
                  parseRankingRewards(
                    so5Leaderboard?.rewardsConfig.ranking
                  ).slice(0, 3) || []
                ).map(ranking => {
                  return (
                    <SBHorizontal key={ranking.startRank}>
                      <Horizontal gap={1.5}>
                        <TrophyIcon ranking={ranking.startRank || 0} />
                        {ranking.toSo5Ranking ? (
                          <>
                            {ranking.toSo5Ranking.user.pictureUrl ? (
                              <img
                                width={14}
                                height={14}
                                style={{ borderRadius: '50%' }}
                                src={ranking.toSo5Ranking.user.pictureUrl || ''}
                                alt=""
                              />
                            ) : (
                              <PlaceHolderAvatar
                                user={ranking.toSo5Ranking.user}
                                variant="extraSmall"
                              />
                            )}
                            <Ellipsis>
                              <LabelM bold>
                                {ranking.toSo5Ranking.user.nickname}
                              </LabelM>
                            </Ellipsis>
                          </>
                        ) : (
                          <ColoredRanking
                            className={`rank-${ranking.startRank}`}
                          >
                            <Ordinal
                              ordinal={ranking.startRank || 0}
                              Sup={BrandSup}
                            />
                          </ColoredRanking>
                        )}
                      </Horizontal>
                      <Rewards gap={0.5}>
                        {ranking.rewardConfigs.map(rewardConfig => (
                          <NewAnyRewardConfig
                            key={rewardConfig.id}
                            anyRewardConfig={rewardConfig}
                            variant={Variant.INLINE}
                          />
                        ))}
                      </Rewards>
                    </SBHorizontal>
                  );
                })}
              </TopLeaderboard>
            );
          })}
        </Scrollable>
        <Dots
          maxDotsToDisplay={5}
          titles={leadearboardsWithRanking.map(leaderboard => leaderboard.slug)}
          setSelectedIndex={setSelectedSo5LeaderboardIndex}
          selectedIndex={selectedSo5LeaderboardIndex}
        />
      </Content>
    </LeagueSquareLink>
  );
};

LivePastLeaderboard.fragments = {
  so5LeaderboardGroup: gql`
    fragment LivePastLeaderboard_so5LeaderboardGroup on So5LeaderboardGroupInterface {
      slug
      so5Leaderboards {
        slug
        division
        mainRarityType
        divisionChangeConfiguration {
          id
        }
        rewardsConfig {
          ranking {
            toSo5Ranking {
              id
              user {
                slug
                pictureUrl(derivative: "avatar")
                nickname
              }
            }
            rewardConfigs {
              id
              ...NewAnyRewardConfig_anyRewardConfigInterface
            }
            ...parseRankingRewards_so5RewardConfig
          }
        }
      }
      ...LeagueSquareLink_so5LeaderboardGroupInterface
    }
    ${NewAnyRewardConfig.fragments.anyRewardConfigInterface}
    ${LeagueSquareLink.fragments.so5LeaderboardGroupInterface}
    ${parseRankingRewards.fragments.so5RewardConfig}
  ` as TypedDocumentNode<LivePastLeaderboard_so5LeaderboardGroup>,
};
